import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Linkedin, Mail, MessageSquare, UserPlus, RefreshCw, Reply, CheckCircle2, XCircle, ShieldOff, Handshake, UserCircle, ExternalLink } from "lucide-react";

const ACTION_META = {
  created: { icon: UserCircle, label: "Added", verb: "Added" },
  invite: { icon: UserPlus, label: "LinkedIn invite", verb: "Invited" },
  accepted: { icon: Handshake, label: "Invite accepted", verb: "Accepted" },
  dm: { icon: MessageSquare, label: "LinkedIn DM", verb: "Messaged" },
  email: { icon: Mail, label: "Cold email", verb: "Emailed" },
  reply: { icon: Reply, label: "Reply received", verb: "Replied" },
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "invite", label: "Invites" },
  { key: "dm", label: "DMs" },
  { key: "email", label: "Emails" },
];

function relTime(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function ActivityPage() {
  const [feed, setFeed] = useState([]);
  const [stats, setStats] = useState({ last_24h: {}, total: 0, last_action_at: null });
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const openTimeline = async (prospectId) => {
    if (!prospectId) return;
    setTimelineOpen(true);
    setTimelineLoading(true);
    setTimeline(null);
    try {
      const { data } = await api.get(`/activity/prospect/${prospectId}`);
      setTimeline(data);
    } catch { setTimeline({ prospect: null, timeline: [] }); }
    setTimelineLoading(false);
  };

  const load = useCallback(async (action = filter) => {
    try {
      const { data } = await api.get(`/activity?limit=150${action ? `&action=${action}` : ""}`);
      setFeed(data.feed || []);
      setStats(data.stats || { last_24h: {} });
    } catch { /* ignore */ }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(filter); }, [filter, load]);
  // Live auto-refresh every 15s
  useEffect(() => {
    const t = setInterval(() => load(filter), 15000);
    return () => clearInterval(t);
  }, [filter, load]);

  const s = stats.last_24h || {};

  return (
    <DashboardLayout title="Activity" eyebrow="Live outreach feed">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="activity-stats">
          <StatTile icon={UserPlus} label="Invites · 24h" value={s.invite || 0} />
          <StatTile icon={MessageSquare} label="DMs · 24h" value={s.dm || 0} />
          <StatTile icon={Mail} label="Emails · 24h" value={s.email || 0} />
          <StatTile icon={Reply} label="Replies · 24h" value={s.reply || 0} />
        </div>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => load(filter)} data-testid="activity-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setFilter(f.key)}
            data-testid={`activity-filter-${f.key || "all"}`}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
              filter === f.key ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="text-xs text-muted-foreground ml-auto">
          {stats.last_action_at ? `Last action ${relTime(stats.last_action_at)}` : "No actions yet"}
        </span>
      </div>

      <div className="border border-border rounded-sm divide-y divide-border" data-testid="activity-feed">
        {loading ? (
          <p className="text-sm text-muted-foreground p-8 text-center">Loading activity…</p>
        ) : feed.length === 0 ? (
          <div className="p-10 text-center" data-testid="activity-empty">
            <p className="text-sm text-muted-foreground">No outreach activity yet.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Once Autonomous Outreach is running, every invite, DM and cold email will appear here in real time — who, when, and the outcome.
            </p>
          </div>
        ) : (
          feed.map((a) => {
            const meta = ACTION_META[a.action] || ACTION_META.email;
            const Icon = a.channel === "linkedin" ? Linkedin : meta.icon;
            const ok = a.status === "ok";
            return (
              <button
                key={a.id}
                onClick={() => openTimeline(a.prospect_id)}
                className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-muted/40 transition-colors"
                data-testid={`activity-row-${a.id}`}
              >
                <div className={`mt-0.5 w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ${
                  a.channel === "linkedin" ? "bg-[#0a66c2]/15 text-[#0a66c2]" : "bg-amber-accent/15 text-amber-accent"
                }`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm">
                    <span className="font-semibold">{meta.verb} </span>
                    <span className="font-semibold">{a.name}</span>
                    {a.title ? <span className="text-muted-foreground"> · {a.title}</span> : null}
                    {a.company ? <span className="text-muted-foreground"> @ {a.company}</span> : null}
                  </div>
                  {a.message_preview ? (
                    <div className="text-xs text-muted-foreground mt-0.5 truncate italic">“{a.message_preview}”</div>
                  ) : null}
                  {a.error ? <div className="text-xs text-destructive mt-0.5">{a.error}</div> : null}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-[0.65rem] text-muted-foreground">{relTime(a.created_at)}</span>
                  <StatusBadge status={a.status} />
                </div>
              </button>
            );
          })
        )}
      </div>

      <ProspectTimelineSheet
        open={timelineOpen}
        onOpenChange={setTimelineOpen}
        data={timeline}
        loading={timelineLoading}
      />
    </DashboardLayout>
  );
}

function ProspectTimelineSheet({ open, onOpenChange, data, loading }) {
  const p = data?.prospect;
  const events = data?.timeline || [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="prospect-timeline-sheet">
        <SheetHeader>
          <SheetTitle>{loading ? "Loading…" : (p?.full_name || "Prospect")}</SheetTitle>
          <SheetDescription className="sr-only">Outreach timeline for this prospect</SheetDescription>
        </SheetHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground mt-6">Loading timeline…</p>
        ) : !p ? (
          <p className="text-sm text-muted-foreground mt-6">Prospect not found.</p>
        ) : (
          <div className="mt-4">
            <div className="text-sm text-muted-foreground">
              {p.title ? <span>{p.title}</span> : null}
              {p.company ? <span> @ {p.company}</span> : null}
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap text-xs">
              <span className="px-2 py-0.5 rounded-sm bg-muted capitalize" data-testid="timeline-status">{(p.status || "new").replace(/_/g, " ")}</span>
              {p.score ? <span className="text-muted-foreground">Fit {p.score}/100</span> : null}
              {p.linkedin_url ? (
                <a href={p.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0a66c2]">
                  <Linkedin className="w-3 h-3" /> LinkedIn <ExternalLink className="w-2.5 h-2.5" />
                </a>
              ) : null}
              {p.email ? <span className="text-muted-foreground truncate">{p.email}</span> : null}
            </div>

            <div className="mt-6 relative pl-6" data-testid="timeline-events">
              <div className="absolute left-[9px] top-1 bottom-1 w-px bg-border" />
              {events.length === 0 ? (
                <p className="text-sm text-muted-foreground">No recorded steps yet.</p>
              ) : (
                events.map((e, i) => {
                  const meta = ACTION_META[e.action] || ACTION_META.email;
                  const Icon = meta.icon;
                  const ok = e.status === "ok";
                  return (
                    <div key={i} className="relative pb-6 last:pb-0">
                      <div className={`absolute -left-6 w-[18px] h-[18px] rounded-full flex items-center justify-center ${
                        ok ? "bg-emerald-500/20 text-emerald-500" : e.status === "blocked" ? "bg-amber-accent/20 text-amber-accent" : "bg-destructive/20 text-destructive"
                      }`}>
                        <Icon className="w-2.5 h-2.5" />
                      </div>
                      <div className="text-sm font-medium">{meta.label}</div>
                      <div className="text-[0.65rem] text-muted-foreground">{relTime(e.at)} · {(e.at || "").slice(0, 16).replace("T", " ")}</div>
                      {e.message ? <div className="text-xs text-muted-foreground mt-1 italic">“{e.message}”</div> : null}
                      {e.error ? <div className="text-xs text-destructive mt-1">{e.error}</div> : null}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StatTile({ icon: Icon, label, value }) {
  return (
    <div className="data-card !p-3 flex items-center gap-3">
      <Icon className="w-4 h-4 text-muted-foreground" />
      <div>
        <div className="text-lg font-display font-semibold leading-none">{value}</div>
        <div className="text-[0.62rem] uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "ok") {
    return <span className="inline-flex items-center gap-1 text-[0.6rem] text-emerald-500"><CheckCircle2 className="w-3 h-3" /> sent</span>;
  }
  if (status === "blocked") {
    return <span className="inline-flex items-center gap-1 text-[0.6rem] text-amber-accent"><ShieldOff className="w-3 h-3" /> blocked</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[0.6rem] text-destructive"><XCircle className="w-3 h-3" /> failed</span>;
}
